'use client';

import type { ReactElement } from 'react';
import { Fragment } from 'react';
import { Controller, type Control } from 'react-hook-form';
import { FormField } from '@/components/inventory/inventory-form-card/shared/FormField';
import type { Contact } from '@/lib/services/contacts/types';
import type { ContactFormSectionProps } from './types';
import { ContactTypeSelector, type ContactType } from './ContactTypeSelector';

interface NameSectionProps extends ContactFormSectionProps {
  contactType: ContactType;
  onTypeChange: (type: ContactType) => void;
}

export function NameSection({
  control,
  mode,
  contactType,
  onTypeChange
}: NameSectionProps): ReactElement {
  const isOrganisation = contactType === 'organisation';

  if (mode === 'read') {
    return <Fragment />;
  }

  return (
    <div>
      <ContactTypeSelector
        contactType={contactType}
        onTypeChange={onTypeChange}
        mode={mode}
      />

      {isOrganisation ? (
        <div>
          <Controller
            control={control as Control<Partial<Contact>>}
            name="organisationName"
            render={({ field, fieldState }) => (
              <FormField
                label="Organisation Name"
                value={(field.value as string) || ''}
                mode="edit"
                placeholder="e.g. The Fred Hollows Foundation"
                onChange={field.onChange}
                error={fieldState.error?.message}
                required
              />
            )}
          />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-3">
            <Controller
              control={control as Control<Partial<Contact>>}
              name="firstName"
              render={({ field, fieldState }) => (
                <FormField
                  label="First Name"
                  value={(field.value as string) || ''}
                  mode="edit"
                  placeholder="Add first name"
                  onChange={field.onChange}
                  error={fieldState.error?.message}
                  required
                />
              )}
            />
            <Controller
              control={control as Control<Partial<Contact>>}
              name="middleName"
              render={({ field, fieldState }) => (
                <FormField
                  label="Middle Name"
                  value={(field.value as string) || ''}
                  mode="edit"
                  placeholder="Add middle name"
                  onChange={field.onChange}
                  error={fieldState.error?.message}
                />
              )}
            />
            <Controller
              control={control as Control<Partial<Contact>>}
              name="lastName"
              render={({ field, fieldState }) => (
                <FormField
                  label="Last Name"
                  value={(field.value as string) || ''}
                  mode="edit"
                  placeholder="Add last name"
                  onChange={field.onChange}
                  error={fieldState.error?.message}
                  required
                />
              )}
            />
          </div>
          {/*
           * The single free-text "Alias" field was retired in LF-5679: other
           * names now live in the "Other names" subsection rendered directly
           * below these fields, which can hold several of them with a role
           * (name in the will, former / maiden, married …) and a reconciliation
           * basis — the structure the court-form alias recital and the
           * share-registry identity union actually need. The `alias` column
           * itself is untouched; AI extraction still writes it and maps it into
           * a name variant.
           */}
        </>
      )}
    </div>
  );
}
